import React, { useState, useRef } from 'react';
import {
  Upload, Type, Highlighter, FileCheck, Wand2,
  Plus, Save, Droplet, Minimize2, FileText
} from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Badge, Alert, ToolActionButtons } from './shared';
import {
  addTextToPDF, addAnnotation, fillPDFForm, extractFormFields,
  addWatermark, compressPDF
} from '../utils/pdfEditorUtils';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../styles/design-system.css';

const PDFAIEditor = () => {
  const [file, setFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('edit');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [textInput, setTextInput] = useState({
    text: '',
    fontSize: 12,
    x: 50,
    y: 700
  });

  const handleFilesSelected = async (files) => {
    if (files && files.length > 0) {
      const selectedFile = files[0];
      setFile(selectedFile);
      setPdfUrl(URL.createObjectURL(selectedFile));
      await loadFormFields(selectedFile);
    }
  };

  const loadFormFields = async (pdfFile) => {
    try {
      setIsProcessing(true);
      setProgress(0);
      const fields = await extractFormFields(pdfFile, setProgress);
      setFormFields(Object.keys(fields).map(key => ({
        name: key,
        ...fields[key]
      })));
      toast.success('Form fields loaded successfully!');
    } catch (error) {
      toast.error(`Failed to load form fields: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFillForm = async () => {
    if (!file) return;

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await fillPDFForm(file, formData, setProgress);
      saveAs(result.blob, result.filename);
      toast.success('Form filled successfully!');
    } catch (error) {
      toast.error(`Failed to fill form: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddText = async () => {
    if (!file || !textInput.text) {
      toast.warning('Please enter text to add');
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await addTextToPDF(
        file,
        textInput.text,
        textInput.x,
        textInput.y,
        { fontSize: textInput.fontSize, pageIndex: 0 }
      );
      saveAs(result.blob, result.filename);
      toast.success('Text added successfully!');
      setTextInput({ text: '', fontSize: 12, x: 50, y: 700 });
    } catch (error) {
      toast.error(`Failed to add text: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddAnnotation = async (type) => {
    if (!file) return;

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await addAnnotation(
        file,
        type,
        { x: 50, y: 700, width: 200, height: 20 },
        { color: type === 'highlight' ? 'yellow' : 'blue', pageIndex: 0 }
      );
      saveAs(result.blob, result.filename);
      toast.success('Annotation added successfully!');
    } catch (error) {
      toast.error(`Failed to add annotation: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWatermark = async () => {
    if (!file) return;

    const watermarkText = prompt('Enter watermark text:');
    if (!watermarkText) return;

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await addWatermark(file, watermarkText, {}, setProgress);
      saveAs(result.blob, result.filename);
      toast.success('Watermark added successfully!');
    } catch (error) {
      toast.error(`Failed to add watermark: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCompress = async () => {
    if (!file) return;

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await compressPDF(file, 'medium', setProgress);
      saveAs(result.blob, result.filename);
      toast.success('PDF compressed successfully!');
    } catch (error) {
      toast.error(`Failed to compress PDF: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const tabs = [
    { id: 'edit', label: 'Text Editor', icon: Type },
    { id: 'annotate', label: 'Annotate', icon: Highlighter },
    { id: 'forms', label: 'Fill Forms', icon: FileCheck },
    { id: 'tools', label: 'Tools', icon: Wand2 },
  ];

  return (
    <div>
      {/* Action Buttons */}
      <ToolActionButtons
        onAdd={() => {
          // Trigger file upload
          document.querySelector('input[type="file"]')?.click();
        }}
        notificationCount={formFields.length}
      />

      {/* Header */}
      {/* Header */}
      <div className="tool-header">
        <div className="tool-icon-wrapper">
          <Type size={40} color="var(--color-text-primary)" />
        </div>
        <div className="tool-header-content">
          <h1 className="tool-title">PDF AI Editor</h1>
          <p className="tool-description">
            Advanced PDF editing with intelligent text recognition, annotations, and form filling capabilities.
          </p>
        </div>
      </div>

      {/* File Upload */}
      {!file ? (
        <FileUploadZone
          onFilesSelected={handleFilesSelected}
          acceptedTypes={['application/pdf']}
          multiple={false}
          title="Drop your PDF file here"
          description="or click to browse"
        />
      ) : (
        <Alert variant="success" title="PDF Loaded">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <FileText size={16} />
            <span>{file.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFile(null);
                setPdfUrl(null);
                setFormFields([]);
                setFormData({});
              }}
            >
              Change PDF
            </Button>
          </div>
        </Alert>
      )}

      {file && (
        <>
          {/* Progress Bar */}
          {isProcessing && (
            <div style={{ marginTop: 'var(--spacing-6)' }}>
              <ProgressBar value={progress} striped showLabel />
            </div>
          )}

          {/* Tabs */}
          <div style={{
            marginTop: 'var(--spacing-6)',
            borderBottom: '1px solid var(--color-border-light)',
            display: 'flex',
            gap: 'var(--spacing-2)'
          }}>
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: 'var(--spacing-3) var(--spacing-6)',
                    border: 'none',
                    background: 'transparent',
                    borderBottom: isActive ? '2px solid var(--color-accent)' : '2px solid transparent',
                    color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                    fontWeight: 'var(--font-semibold)',
                    cursor: 'pointer',
                    transition: 'all var(--transition-base)'
                  }}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div style={{ marginTop: 'var(--spacing-6)' }}>
            <Card>
              {activeTab === 'edit' && (
                <div>
                  <h3 style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-bold)',
                    marginBottom: 'var(--spacing-4)'
                  }}>
                    Add Text to PDF
                  </h3>
                  <div className="grid-2">
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        marginBottom: 'var(--spacing-2)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        Text Content
                      </label>
                      <input
                        type="text"
                        placeholder="Enter text to add"
                        value={textInput.text}
                        onChange={(e) => setTextInput({ ...textInput, text: e.target.value })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        marginBottom: 'var(--spacing-2)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        Font Size
                      </label>
                      <input
                        type="number"
                        placeholder="Font size"
                        value={textInput.fontSize}
                        onChange={(e) => setTextInput({ ...textInput, fontSize: parseInt(e.target.value) })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        marginBottom: 'var(--spacing-2)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        X Position
                      </label>
                      <input
                        type="number"
                        placeholder="X position"
                        value={textInput.x}
                        onChange={(e) => setTextInput({ ...textInput, x: parseInt(e.target.value) })}
                        className="input"
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        marginBottom: 'var(--spacing-2)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        Y Position
                      </label>
                      <input
                        type="number"
                        placeholder="Y position"
                        value={textInput.y}
                        onChange={(e) => setTextInput({ ...textInput, y: parseInt(e.target.value) })}
                        className="input"
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: 'var(--spacing-4)' }}>
                    <Button
                      variant="primary"
                      onClick={handleAddText}
                      disabled={isProcessing}
                      loading={isProcessing}
                    >
                      <Plus size={18} />
                      Add Text to PDF
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === 'annotate' && (
                <div>
                  <h3 style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-bold)',
                    marginBottom: 'var(--spacing-4)'
                  }}>
                    Add Annotations
                  </h3>
                  <div className="grid-3">
                    <Card variant="outlined" hoverable onClick={() => handleAddAnnotation('highlight')}>
                      <div style={{ textAlign: 'center' }}>
                        <Highlighter size={32} color="var(--color-warning)" style={{ margin: '0 auto var(--spacing-3)' }} />
                        <h4 style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-1)' }}>
                          Highlight
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                          Add yellow highlights
                        </p>
                      </div>
                    </Card>
                    <Card variant="outlined" hoverable onClick={() => handleAddAnnotation('underline')}>
                      <div style={{ textAlign: 'center' }}>
                        <Type size={32} color="var(--color-info)" style={{ margin: '0 auto var(--spacing-3)' }} />
                        <h4 style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-1)' }}>
                          Underline
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                          Underline text
                        </p>
                      </div>
                    </Card>
                    <Card variant="outlined" hoverable onClick={() => handleAddAnnotation('box')}>
                      <div style={{ textAlign: 'center' }}>
                        <FileText size={32} color="var(--color-success)" style={{ margin: '0 auto var(--spacing-3)' }} />
                        <h4 style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-1)' }}>
                          Box
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                          Draw boxes
                        </p>
                      </div>
                    </Card>
                  </div>
                </div>
              )}

              {activeTab === 'forms' && (
                <div>
                  <h3 style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-bold)',
                    marginBottom: 'var(--spacing-4)'
                  }}>
                    Fill Form Fields
                  </h3>
                  {formFields.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                      {formFields.map((field, index) => (
                        <div key={index}>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-medium)',
                            marginBottom: 'var(--spacing-2)',
                            color: 'var(--color-text-secondary)'
                          }}>
                            {field.name} {field.required && <Badge variant="error">Required</Badge>}
                          </label>
                          <input
                            type="text"
                            value={formData[field.name] || ''}
                            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                            className="input"
                            placeholder={`Enter ${field.name}`}
                          />
                        </div>
                      ))}
                      <div>
                        <Button
                          variant="primary"
                          onClick={handleFillForm}
                          disabled={isProcessing}
                          loading={isProcessing}
                        >
                          <Save size={18} />
                          Save Filled Form
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Alert variant="info">
                      No form fields detected in this PDF. Upload a PDF with fillable forms to use this feature.
                    </Alert>
                  )}
                </div>
              )}

              {activeTab === 'tools' && (
                <div>
                  <h3 style={{
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-bold)',
                    marginBottom: 'var(--spacing-4)'
                  }}>
                    PDF Tools
                  </h3>
                  <div className="grid-2">
                    <Card variant="outlined" hoverable onClick={handleWatermark}>
                      <div style={{ textAlign: 'center' }}>
                        <Droplet size={32} color="var(--color-accent)" style={{ margin: '0 auto var(--spacing-3)' }} />
                        <h4 style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-1)' }}>
                          Add Watermark
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                          Add text watermark to PDF
                        </p>
                      </div>
                    </Card>
                    <Card variant="outlined" hoverable onClick={handleCompress}>
                      <div style={{ textAlign: 'center' }}>
                        <Minimize2 size={32} color="var(--color-secondary)" style={{ margin: '0 auto var(--spacing-3)' }} />
                        <h4 style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-1)' }}>
                          Compress PDF
                        </h4>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                          Reduce file size
                        </p>
                      </div>
                    </Card>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* PDF Preview */}
          {pdfUrl && (
            <div style={{ marginTop: 'var(--spacing-6)' }}>
              <Card>
                <h3 style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-bold)',
                  marginBottom: 'var(--spacing-4)'
                }}>
                  PDF Preview
                </h3>
                <iframe
                  src={pdfUrl}
                  style={{
                    width: '100%',
                    height: '600px',
                    border: '1px solid var(--color-border-light)',
                    borderRadius: 'var(--radius-lg)'
                  }}
                  title="PDF Preview"
                />
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PDFAIEditor;

